/** Media routes (IA §2 Operate › Activity › Video). Every view's state is in the URL. */
import { createRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { appRoute } from '@/app/appRoute';
import { str } from '@/lib/search';
import { isIsoDay } from '@/lib/timeRange';

export type VideoTab = 'library' | 'requests' | 'availability' | 'automatic';
const TABS: VideoTab[] = ['library', 'requests', 'availability', 'automatic'];

export interface VideoSearch {
  tab?: VideoTab;
  vehicle?: string;
  driver?: string;
  channel?: 'forward' | 'inward';
  event?: string;
  deleted?: 'yes';
  status?: 'active' | 'done';
  origin?: 'user' | 'automation';
  day?: string;
  video?: string;
  request?: string;
  sort?: string;
}

export const videoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/activity/video',
  staticData: { permission: 'media.video.read' },
  validateSearch: (s: Record<string, unknown>): VideoSearch => ({
    tab: TABS.includes(s.tab as VideoTab) ? (s.tab as VideoTab) : undefined,
    vehicle: str(s.vehicle),
    driver: str(s.driver),
    channel: s.channel === 'forward' || s.channel === 'inward' ? s.channel : undefined,
    event: str(s.event),
    deleted: s.deleted === 'yes' ? 'yes' : undefined,
    status: s.status === 'active' || s.status === 'done' ? s.status : undefined,
    origin: s.origin === 'user' || s.origin === 'automation' ? s.origin : undefined,
    day: isIsoDay(s.day) ? s.day : undefined,
    video: str(s.video),
    request: str(s.request),
    sort: str(s.sort),
  }),
  component: lazyRouteComponent(() => import('./pages/VideoPage')),
});

export interface RequestSearch {
  vehicle?: string;
  detection?: string;
  /** Clip start (ISO instant). */
  start?: string;
  seconds?: number;
  channels?: string;
  day?: string;
}

export const requestRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/activity/video/request',
  staticData: { permission: 'media.video.read' },
  validateSearch: (s: Record<string, unknown>): RequestSearch => {
    const secs = Number(s.seconds);
    const start = str(s.start);
    return {
      vehicle: str(s.vehicle),
      detection: str(s.detection),
      start: start && !Number.isNaN(new Date(start).getTime()) ? start : undefined,
      seconds: Number.isFinite(secs) && secs > 0 ? Math.round(secs) : undefined,
      channels: str(s.channels),
      day: isIsoDay(s.day) ? s.day : undefined,
    };
  },
  component: lazyRouteComponent(() => import('./pages/RequestPage')),
});

/** Links the server writes (`/media?vehicle=`, `/media/videos/<id>`) and the old console path. */
export const mediaAliasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/media',
  validateSearch: (s: Record<string, unknown>): { vehicle?: string } => ({ vehicle: str(s.vehicle) }),
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/activity/video', search: { tab: search.vehicle ? 'requests' : undefined, vehicle: search.vehicle } });
  },
});

export const mediaVideoAliasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/media/videos/$videoId',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/activity/video', search: { video: params.videoId } });
  },
});

export const mediaModuleAliasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/media-module',
  beforeLoad: () => {
    throw redirect({ to: '/activity/video' });
  },
});
